'use client';

import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Box, FormLabel, MenuItem, TextField, Typography } from '@mui/material';
import { DatePicker } from '@/components/ui/date-picker';

interface InvoiceDetailsFields {
  document_type: 'invoice' | 'quote';
  invoice_number: string;
  subject: string;
  issue_date: string;
  due_date: string;
  service_from: string;
  service_to: string;
  po_number: string;
  sales_person: string;
  terms: string;
}

interface InvoiceDetailsCardProps {
  formData: InvoiceDetailsFields;
  onChange: (field: keyof InvoiceDetailsFields, value: string) => void;
  onDocumentTypeChange: (value: string) => void;
  isConvertingFromQuote: boolean;
  isGeneratingInvoiceNumber: boolean;
  users: any[];
  selectedSalesPerson: any | null;
  onSalesPersonChange: (value: string) => void;
  /**
   * Whether the current user may name a DIFFERENT salesperson. Only an admin
   * (super_admin) can; a non-admin is locked to themselves (the field renders
   * disabled). crm-api enforces the same rule — see InvoicesController. Defaults
   * to true so existing callers are unchanged.
   */
  canEditSalesPerson?: boolean;
  /**
   * The CURRENT signed-in user's display name. When the field is locked (a
   * non-admin), it shows THIS — never the loaded document's `sales_person`,
   * which on an edit/duplicate is the SOURCE doc's (possibly someone else's)
   * salesperson. "Set to you automatically" must mean YOU.
   */
  lockedSalesPersonName?: string;
  /** Server-side validation message for the due date, anchored to the field. */
  dueDateError?: string;
}

export const InvoiceDetailsCard: React.FC<InvoiceDetailsCardProps> = ({
  formData,
  onChange,
  onDocumentTypeChange,
  isConvertingFromQuote,
  isGeneratingInvoiceNumber,
  users,
  selectedSalesPerson,
  onSalesPersonChange,
  canEditSalesPerson = true,
  lockedSalesPersonName,
  dueDateError,
}) => {
  // Display name for the locked (non-admin) Sales Person field. Prefer the CURRENT
  // user's own name (`lockedSalesPersonName`) — the field is "set to you
  // automatically", so it must be YOU, never the loaded document's `sales_person`
  // (on an edit/duplicate that is the SOURCE doc's salesperson, e.g. a stale
  // "j.smits.home"). Only if that isn't available do we fall back to the selected
  // person's record, then the frozen form value, then the email.
  const salesPersonDisplayName =
    lockedSalesPersonName ||
    (selectedSalesPerson &&
      (selectedSalesPerson.full_name ||
        selectedSalesPerson.name ||
        `${selectedSalesPerson.first_name || ''} ${selectedSalesPerson.last_name || ''}`.trim())) ||
    formData.sales_person ||
    selectedSalesPerson?.email ||
    '';
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography component="h3" sx={{ fontSize: "1.125rem", fontWeight: 600 }}>Document Details</Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box>
          <FormLabel htmlFor="document_type">Document Type *</FormLabel>
          <TextField
            select
            id="document_type"
            size="small"
            fullWidth
            value={formData.document_type}
            onChange={(e) => onDocumentTypeChange(e.target.value)}
            disabled={isConvertingFromQuote}
          >
            <MenuItem value="invoice">Invoice</MenuItem>
            <MenuItem value="quote">Quote</MenuItem>
          </TextField>
          {isConvertingFromQuote && (
            <Typography component="p" sx={{ fontSize: 14, color: "primary.main", mt: 0.5 }}>Converting quote to invoice</Typography>
          )}
        </Box>
        <Box>
          <FormLabel htmlFor="invoice_number">{formData.document_type === 'quote' ? 'Quote Number *' : 'Invoice Number *'}</FormLabel>
          <TextField
            id="invoice_number"
            size="small"
            fullWidth
            value={isGeneratingInvoiceNumber ? 'Generating...' : formData.invoice_number}
            onChange={(e) => onChange('invoice_number', e.target.value)}
            placeholder={isGeneratingInvoiceNumber ? `Generating ${formData.document_type} number...` : `e.g., 700-0019-${formData.document_type === 'quote' ? '500' : '041'}`}
            disabled={isGeneratingInvoiceNumber}
            {...(isGeneratingInvoiceNumber ? { sx: { "& .MuiInputBase-input": { color: "primary.main" } } } : {})}
          />
        </Box>
        {/* P.O. Number - Only show for invoices, not quotes */}
        {formData.document_type === 'invoice' && (
          <Box>
            <FormLabel htmlFor="po_number">P.O. Number</FormLabel>
            <TextField
              id="po_number"
              size="small"
              fullWidth
              value={formData.po_number}
              onChange={(e) => onChange('po_number', e.target.value)}
              placeholder="Purchase Order Number"
            />
          </Box>
        )}
        <Box>
          <FormLabel>{formData.document_type === 'quote' ? 'Quote Date *' : 'Invoice Date *'}</FormLabel>
          <DatePicker
            value={formData.issue_date}
            onChange={(date) => onChange('issue_date', date)}
          />
        </Box>
        {/* Due Date - Only show for invoices, not quotes. The label, the
            picker AND the server-error message all live in this one block
            so the message stays anchored to the field. */}
        {formData.document_type === 'invoice' && (
          <Box>
            <FormLabel>Due Date *</FormLabel>
            <DatePicker
              value={formData.due_date}
              onChange={(date) => onChange('due_date', date)}
              {...(dueDateError ? { sx: { "& .MuiOutlinedInput-notchedOutline": { borderColor: "error.main" }, "&:focus-within .MuiOutlinedInput-notchedOutline": { borderColor: "error.main" } } } : {})}
            />
            {dueDateError && (
              <Typography component="p" sx={{ fontSize: 14, color: "error.main", mt: 1, display: "flex", alignItems: "flex-start", gap: 0.5, whiteSpace: "pre-line" }}>
                <Box component="span" sx={{ flexShrink: 0, mt: "2px", display: "flex" }}><AlertTriangle size={16} /></Box>
                <span>{dueDateError}</span>
              </Typography>
            )}
          </Box>
        )}
        {/* Service Period - Only show for invoices, not quotes. Service From
            / Service To sit side by side in ONE row — a single 2-column grid
            wrapper. invoice-details-card.test.tsx pins this row (by its
            data-testid + computed display:grid) so the fields can't drift
            back to stacked. */}
        {formData.document_type === 'invoice' && (
          <Box data-testid="service-period-row" sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
            <Box>
              <FormLabel>Service From</FormLabel>
              <DatePicker
                value={formData.service_from}
                onChange={(date) => onChange('service_from', date)}
              />
            </Box>
            <Box>
              <FormLabel>Service To</FormLabel>
              <DatePicker
                value={formData.service_to}
                onChange={(date) => onChange('service_to', date)}
              />
            </Box>
          </Box>
        )}
        <Box>
          <FormLabel htmlFor="sales_person">Sales Person</FormLabel>
          {canEditSalesPerson ? (
            // Admin: free picker of the whole assignable roster.
            <TextField
              select
              id="sales_person"
              size="small"
              fullWidth
              SelectProps={{ displayEmpty: true }}
              value={selectedSalesPerson?.id?.toString() || ''}
              onChange={(e) => onSalesPersonChange(e.target.value)}
            >
              {users.length > 0 ? (
                users.map((user) => (
                  <MenuItem key={user.id} value={user.id.toString()}>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Box component="span" sx={{ fontWeight: 500 }}>{user.full_name || user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim()}</Box>
                      {user.email && <Box component="span" sx={{ fontSize: 12, color: "text.secondary" }}>{user.email}</Box>}
                    </Box>
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No users found
                </MenuItem>
              )}
            </TextField>
          ) : (
            // Non-admin: locked to self. Render a plain disabled field showing the
            // current user's OWN name — NOT a <Select>. A disabled Select renders
            // empty whenever its value has no matching <MenuItem>, and the signed-in
            // rep is routinely ABSENT from /users/salespeople: that roster is crm-api's
            // `assignable_roster` (active, non-service users) and the dashboard-provisioned
            // staff mirror is commonly inactive, so its id never matches a menu item and
            // the field showed blank. `selectedSalesPerson` is the email-resolved human
            // (from /auth/me), so its name is reliable independent of the roster list.
            <TextField
              id="sales_person"
              size="small"
              fullWidth
              disabled
              value={salesPersonDisplayName}
              helperText="Set to you automatically — only an admin can reassign."
              InputProps={{ readOnly: true }}
            />
          )}
        </Box>
        <Box>
          <FormLabel htmlFor="terms">Terms</FormLabel>
          <TextField
            id="terms"
            size="small"
            fullWidth
            value={formData.terms}
            onChange={(e) => onChange('terms', e.target.value)}
            placeholder="Due upon receipt of Invoice"
          />
        </Box>
        <Box>
          <FormLabel htmlFor="subject">Description</FormLabel>
          <TextField
            id="subject"
            size="small"
            fullWidth
            value={formData.subject}
            onChange={(e) => onChange('subject', e.target.value)}
            placeholder={`Brief description of the ${formData.document_type === 'quote' ? 'quote' : 'invoice'}`}
          />
        </Box>
      </Box>
    </Box>
  );
};
